'use client';

import { useOverlayTriggerState } from '@react-stately/overlays';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import styled from 'styled-components';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { BlockTemplateBase, SerializedBlock } from '@zoomit/editor';
import { AlarmAdd, ArrowDown, ArrowLeft, ArrowUp, CheckOutlined, PlayCircle } from '@zoomit/icons';
import type { ArrayElement } from '@zoomit/primitives';
import { Box, cn, Col, Flex, Image, Spacing } from '@zoomit/primitives';
import type { BaseImageType, ProductListItemDetailsType, ReviewInfoResponseType } from '@zoomit/web-kit';
import {
  ArticleParser,
  isMobile,
  OverviewProsCons,
  PriceNotifyModal,
  ProductStoreListItemDesktop,
  ProductStoreListItemMobile,
  useGallery,
  usePriceNotify,
} from '@zoomit/web-kit';
import { Anchor, Button, cssVar, elevation, theme, Typography } from '@zoomit/zoomit-ds';

import { useGenerateSingleProductHref } from '../../../hooks/catalog/useGenerateSingleProductHref';
import { useOrigin } from '../../../providers/OriginProvider';
import styles from './RelatedProductSingleItem.module.scss';
import SpecCol from './SpecCol';

const Gallery = dynamic(() => import('@zoomit/web-kit').then((mod) => mod.Gallery), {
  ssr: false,
});

type Props = {
  template: ArrayElement<BlockTemplateBase['related-product']['subTemplates']>['value'];
  productData: ProductListItemDetailsType;
  reviewData: ReviewInfoResponseType | undefined;
};

const RelatedProductSingleItem = (props: Props) => {
  const { productData, template } = props;

  const firstMedia = productData.gallery[0];

  const otherImages = productData.gallery.slice(1, 5);

  const showMoreDescriptionState = useOverlayTriggerState({});

  const firstTwoDescriptionBlocks = productData.overview.description?.slice(0, 2);
  const otherDescriptionBlocks = productData.overview.description?.slice(2);

  const [visibleDescriptionBlocks, setVisibleDescriptionBlocks] =
    useState<SerializedBlock[]>(firstTwoDescriptionBlocks);

  const pagination = {
    dynamicBullets: true,
    dynamicMainBullets: Math.min(5, productData.gallery.length),
    renderBullet: function (_: number, className: string) {
      return `<span class='${className}'></span>`;
    },
  };

  const { priceNotifyPopOverState, notifyQuery, deleteMutation, postMutation } = usePriceNotify({
    productId: productData.id,
  });

  const { handleCloseGallery, handleOpenGallery, isGalleryOpen } = useGallery({ galleryId: productData.id });

  const { baseMediaUrl } = useOrigin();
  const href = useGenerateSingleProductHref(productData.slug);

  return (
    <>
      <Box className={cn(!isMobile && styles.wrapper)} spacing={{ py: isMobile ? 'x6' : 'x12' }}>
        <Box spacing={{ px: isMobile ? 'x0' : 11.2 }}>
          <Title>
            <Typography
              spacing={{ mr: isMobile ? 'x0' : 'x4' }}
              tag="div"
              fontSize={1.6}
              fontWeight="bold"
              lineHeight={1.75}
            >
              {productData.title}
            </Typography>
          </Title>
        </Box>
        <Box spacing={{ px: isMobile ? 'x0' : 11.2 }}>
          <Box spacing={{ my: 'x8' }}>
            {isMobile ? (
              <SwiperEl modules={[Pagination]} slidesPerView={1} spaceBetween={16} pagination={pagination}>
                {productData.gallery.slice(0, 5).map((media, index) => (
                  <SwiperSlide key={index}>
                    <ImageContainer onClick={() => handleOpenGallery(index)}>
                      <Image
                        src={`${baseMediaUrl}/${
                          media.type === 'Image' ? media.image.id : media.video.coverImage.id
                        }`}
                        fill
                        alt={media.type === 'Image' ? media.image.alt : media.video.coverImage.alt}
                        placeholder="blur"
                        blurDataURL={
                          media.type === 'Image' ? media.image.preview : media.video.coverImage.preview
                        }
                        style={{
                          objectFit: 'contain',
                        }}
                        sizes="100vw"
                      />
                      {media.type === 'Video' && (
                        <PlayCircleWrapper>
                          <PlayCircle size="xl" fill={cssVar(theme.white)} />
                        </PlayCircleWrapper>
                      )}
                    </ImageContainer>
                  </SwiperSlide>
                ))}
              </SwiperEl>
            ) : (
              <Flex style={{ height: '21.4rem' }} gap={1}>
                <Flex style={{ position: 'relative', width: 'calc(50% - 5px)' }}>
                  <CustomImage
                    src={`${baseMediaUrl}/${
                      firstMedia.type === 'Image' ? firstMedia.image.id : firstMedia.video.coverImage.id
                    }`}
                    alt={firstMedia.type === 'Image' ? firstMedia.image.alt : firstMedia.video.coverImage.alt}
                    placeholder="blur"
                    blurDataURL={
                      firstMedia.type === 'Image'
                        ? firstMedia.image.preview
                        : firstMedia.video.coverImage.preview
                    }
                    fill
                    sizes="40vw"
                    style={{ objectFit: 'contain' }}
                    onClick={() => handleOpenGallery(0)}
                  />
                </Flex>
                <Flex style={{ width: 'calc(50% - 5px)', gap: '10px 8px' }}>
                  {otherImages.map((media, index) => (
                    <Flex
                      key={`related-product-${index}`}
                      style={{
                        position: 'relative',
                        flex: 'calc(50% - 10px) 1 1',
                        height: 'calc(50% - 5px)',
                      }}
                    >
                      <CustomImage
                        key={media.type === 'Image' ? media.image.alt : media.video.coverImage.alt}
                        src={`${baseMediaUrl}/${
                          media.type === 'Image' ? media.image.id : media.video.coverImage.id
                        }`}
                        alt={media.type === 'Image' ? media.image.alt : media.video.coverImage.alt}
                        placeholder="blur"
                        blurDataURL={
                          media.type === 'Image' ? media.image.preview : media.video.coverImage.preview
                        }
                        fill
                        sizes="20vw"
                        style={{ objectFit: 'contain' }}
                        onClick={() => handleOpenGallery(index + 1)}
                      />
                      {media.type === 'Video' && (
                        <PlayCircleWrapper>
                          <PlayCircle size="xl" fill={cssVar(theme.white)} />
                        </PlayCircleWrapper>
                      )}
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}
          </Box>
          <Flex
            gap={Spacing.x2}
            justifyContent="space-between"
            alignItems="center"
            flexWrap={isMobile ? 'wrap' : 'nowrap'}
            spacing={{ mb: 'x8' }}
          >
            {productData.keySpecifications.map((ks, index) => (
              <SpecCol key={`key-specification-item-${index}`} {...ks} />
            ))}
          </Flex>
          {template === 'single_product_complete' && (
            <ParserCol spacing={{ mb: firstTwoDescriptionBlocks?.length > 0 ? 'x9' : 'x0' }}>
              {firstTwoDescriptionBlocks?.length > 0 && (
                <>
                  <ArticleParser articleData={visibleDescriptionBlocks} hasSidebar={false} />
                  {otherDescriptionBlocks?.length > 0 && (
                    <Button
                      rightIcon={
                        showMoreDescriptionState.isOpen ? (
                          <ArrowUp fill={cssVar(theme.primary1)} size="sm" />
                        ) : (
                          <ArrowDown fill={cssVar(theme.primary1)} size="sm" />
                        )
                      }
                      variant="text"
                      size="auto"
                      spacing={{ px: 'x0', mt: 'x3' }}
                      onClick={() => {
                        showMoreDescriptionState.isOpen
                          ? setVisibleDescriptionBlocks(firstTwoDescriptionBlocks)
                          : setVisibleDescriptionBlocks(productData.overview.description);
                        showMoreDescriptionState.toggle();
                      }}
                    >
                      {showMoreDescriptionState.isOpen ? 'بستن توضیحات' : 'مشاهده توضیحات بیشتر'}
                    </Button>
                  )}
                </>
              )}
            </ParserCol>
          )}
          {(template === 'single_product_complete' || template === 'single_product_pros_cons') && (
            <Box
              spacing={{
                mb:
                  productData.overview.pros?.length > 0 || productData.overview.cons?.length > 0
                    ? 'x8'
                    : 'x0',
              }}
            >
              <OverviewProsCons pros={productData.overview.pros} cons={productData.overview.cons} />
            </Box>
          )}
          <Box
            backgroundColor={isMobile ? cssVar(theme.background1) : cssVar(theme.background2)}
            spacing={{ mx: isMobile ? -1.6 : 'x0', p: isMobile ? 'x4' : 'x0' }}
          >
            <Title>
              <Typography
                spacing={{ mr: isMobile ? 'x0' : 'x4' }}
                tag="div"
                fontSize={1.6}
                fontWeight="bold"
                lineHeight={1.75}
              >
                قیمت و فروشندگان
              </Typography>
            </Title>
            {productData.stores.items.length > 0 ? (
              <Box spacing={{ mb: isMobile ? 'x2' : 'x6', mt: 'x4' }}>
                <Flex
                  flexDirection={isMobile ? 'row' : 'column'}
                  gap={isMobile ? Spacing.x4 : Spacing.x2}
                  spacing={{ mb: 'x4', px: 'x' }}
                  style={{ overflowX: isMobile ? 'auto' : 'unset' }}
                  flexWrap="nowrap"
                >
                  {productData.stores.items.map((store, index) =>
                    isMobile ? (
                      <div key={`product-stores-${store.storeId}`} style={{ width: '27.3rem' }}>
                        <ProductStoreListItemMobile
                          positionId={index}
                          isMorePurchaseItemsVisible={false}
                          {...store}
                        />
                      </div>
                    ) : (
                      <ProductStoreListItemDesktop
                        positionId={index}
                        key={`product-stores-${store.storeId}`}
                        {...store}
                      />
                    ),
                  )}
                </Flex>
                {productData.stores.totalCount - productData.stores.items.length > 0 && (
                  <Flex
                    gap={isMobile ? Spacing.x4 : Spacing.x0}
                    flexDirection={isMobile ? 'column' : 'row'}
                    justifyContent="space-between"
                    alignItems={isMobile ? 'flex-start' : 'center'}
                  >
                    <div>
                      <Typography className="fa" fontWeight="bold" textColor={cssVar(theme.text)}>
                        {productData.stores.totalCount - productData.stores.items.length}
                      </Typography>
                      <Typography textColor={cssVar(theme.grey1)} className="fa">
                        {' '}
                        فروشنده دیگر نیز این محصول را دارند{' '}
                      </Typography>
                    </div>
                    <Anchor
                      size={isMobile ? 'full width' : 'fit'}
                      href={
                        productData.stores.totalCount - productData.stores.items.length > 0
                          ? `${href}#price`
                          : href
                      }
                      variant={isMobile ? 'contained' : 'outline'}
                      leftIcon={<ArrowLeft size="sm" className="!fill-primary1" />}
                    >
                      مشاهده همه فروشندگان
                    </Anchor>
                  </Flex>
                )}
              </Box>
            ) : (
              <EmptyFlexBox
                flexDirection="column"
                justifyContent="center"
                spacing={{ mb: 'x6', mt: 'x4' }}
                alignItems="center"
                gap={Spacing.x4}
              >
                <Typography fontSize={1.4} fontWeight="bold" lineHeight={2} textColor={cssVar(theme.grey1)}>
                  این محصول هم‌اکنون در بازار موجود نیست
                </Typography>
                {notifyQuery.data ? (
                  <Button
                    variant="outline"
                    size="fit"
                    fontWeight="normal"
                    rightIcon={<CheckOutlined className="!fill-success1" size="sm" />}
                    onClick={() => deleteMutation.mutate()}
                  >
                    <Typography fontSize={1.2} fontWeight="bold">
                      موجود شد خبرت می‌کنیم
                    </Typography>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="fit"
                    fontWeight="normal"
                    rightIcon={<AlarmAdd className="!fill-text" size="sm" />}
                    onClick={priceNotifyPopOverState.open}
                  >
                    <Typography fontSize={1.2} textColor={cssVar(theme.text)} fontWeight="bold">
                      موجود شد خبرم کن
                    </Typography>
                  </Button>
                )}
              </EmptyFlexBox>
            )}
          </Box>
        </Box>
        {isGalleryOpen && (
          <Gallery
            baseMediaUrl={baseMediaUrl}
            items={productData.gallery}
            onCloseGallery={handleCloseGallery}
          />
        )}
      </Box>
      <PriceNotifyModal
        productId={productData.id}
        title={productData.title}
        image={productData.gallery[0].image as BaseImageType}
        onClose={priceNotifyPopOverState.close}
        isPriceNotifyShow={priceNotifyPopOverState.isOpen}
        onSetNotify={() => postMutation.mutate()}
      />
    </>
  );
};

export default RelatedProductSingleItem;

const CustomImage = styled(Image)`
  ${elevation};
  border-radius: var(${theme.radius.sm});
  cursor: pointer;
`;

const ParserCol = styled(Col)`
  div {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  @media (min-width: 1416px) {
    div {
      width: 100%;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
  }
  @media (min-width: 992px) {
    div {
      max-width: unset !important;
    }
  }
`;

const Title = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  h5 {
    padding-right: 1.6rem;
  }

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 100%;
    top: 0;
    background-color: var(${theme.primary1});
    ${isMobile && 'right: -16px'}
  }
`;

const EmptyFlexBox = styled(Flex)`
  border: 1px solid #c8c8cd;
  border-radius: var(${theme.radius.sm});
  width: 100%;
  height: 15.9rem;
`;

const SwiperEl = styled(Swiper)`
  position: relative;
  padding-bottom: 30px;

  .swiper-pagination-bullets {
    position: absolute;
    bottom: 0;
    z-index: 99;
  }

  .swiper-pagination-bullet-active {
    background: var(${theme.primary1});
  }
`;

const ImageContainer = styled.div`
  ${elevation};
  border-radius: var(${theme.radius.sm});
  position: relative;
  height: 20rem;
`;

const PlayCircleWrapper = styled.div`
  z-index: 9;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
